import { useEffect, useMemo, useState } from 'react';
import { ensureModelViewer } from '@/lib/ensureModelViewer';
import { Trans, useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertTriangle, Building2, Check, Copy, DollarSign, Flame, Globe,
  Hash, Home, Layers, Mail, MapPin, MessageCircle, MessageSquare,
  Search, Share2, Stethoscope, Users,
} from 'lucide-react';

// ────────────────────────────────────────────────────────────────────────────
// Scope = "thing the user could share". Each scope has its own URL + templates.
// ────────────────────────────────────────────────────────────────────────────
interface ShareScope {
  id: string;
  category: 'breakdown' | 'hospitals' | 'codes' | 'scenarios' | 'international' | 'investigate' | 'engage' | 'site';
  label: string;
  icon: React.ReactNode;
  url: string;
  description?: string;
  templates: Array<{ id: 'outrage' | 'curiosity' | 'educational' | 'civic' | 'personal'; label: string; text: string }>;
}

// Full props now optional — modal can be opened from anywhere with a base URL
interface ShareDignityModalProps {
  isOpen: boolean;
  onClose: () => void;

  /** Direct URL to share. Used to build the "current view" scope. */
  shareUrl?: string;
  /** Optional context that lets the modal offer richer/more-specific scopes. */
  hospitalId?: string;
  hospitalName?: string;
  hospitalState?: string;
  codeType?: string; // 'cpt' | 'hcpcs' | 'rev' | 'drg' | 'ndc' | 'drug' | 'supply'
  code?: string;
  codeTypeLabel?: string;
  markupPercentage?: number | null;
  scenarioId?: string;
  scenarioSlug?: string;

  /** Override the default scope. If absent, defaults to the most specific one available. */
  defaultScopeId?: string;
}

const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
// Bare host for share copy. Shared URLs already follow ORIGIN, but the prose
// templates used to name americanhospitalprices.com literally — which meant a
// share from Luigi's or AntiGreed advertised the sibling brand.
const DOMAIN = ORIGIN.replace(/^https?:\/\//, '');
const enc = (s: string) => encodeURIComponent(s);
const abs = (path: string) => (path.startsWith('http') ? path : `${ORIGIN}${path}`);
const codeUrlSlug = (t?: string) => {
  const map: Record<string, string> = { cpt: 'cpt', hcpcs: 'hcpcs', drg: 'drg', ndc: 'ndc', revenue: 'rev', supply: 'supply', drug: 'drugs' };
  return t ? (map[t] || t) : '';
};

// Build the list of available scopes given the modal's context props
function buildScopes(p: ShareDignityModalProps): ShareScope[] {
  const out: ShareScope[] = [];
  const hosp = p.hospitalName?.replace(/\s+/g, ' ').trim() || 'this hospital';
  const typeLabel = p.codeTypeLabel?.toUpperCase() || 'these';
  const mk = p.markupPercentage != null && Number.isFinite(p.markupPercentage)
    ? Math.round(Number(p.markupPercentage)) : null;

  // 1) The exact view (current breakdown) — only if shareUrl is provided
  if (p.shareUrl) {
    out.push({
      id: 'breakdown',
      category: 'breakdown',
      label: 'This view',
      icon: <Layers className="w-3.5 h-3.5" />,
      url: p.shareUrl,
      description: 'The exact breakdown you\'re looking at right now.',
      templates: [
        {
          id: 'outrage', label: 'Outrage hook',
          text: mk != null && mk > 0
            ? `${hosp} bills ${mk}% above the national average for ${typeLabel} procedures. These are real prices from their published chargemaster — what uninsured patients actually pay. ${p.shareUrl}`
            : `${hosp}'s prices are public — and people should look. These are real chargemaster numbers, not estimates: ${p.shareUrl}`,
        },
        { id: 'curiosity', label: 'Curiosity', text: `The same procedure can cost 40× more at one hospital than the next. ${hosp}'s prices are public — see where they land: ${p.shareUrl}` },
        { id: 'educational', label: 'Educational', text: `Hospital prices in the US are public by federal law (Hospital Price Transparency, 2021). Most people never look. Here's what ${hosp} actually charges: ${p.shareUrl}` },
        { id: 'civic', label: 'Civic', text: `Healthcare price transparency is a fight, not a finished thing. The more eyes on these published prices, the better. Look at ${hosp}: ${p.shareUrl}` },
      ],
    });
  }

  // 2) Hospital profile
  if (p.hospitalId) {
    const url = abs(`/hospital/${p.hospitalId}`);
    out.push({
      id: 'hospital',
      category: 'hospitals',
      label: hosp.length > 24 ? hosp.slice(0, 22) + '…' : hosp,
      icon: <Building2 className="w-3.5 h-3.5" />,
      url,
      description: `Full chargemaster + ranking for ${hosp}.`,
      templates: [
        { id: 'outrage', label: 'Outrage hook', text: `${hosp}'s full chargemaster is public — every billed code, every price they actually charge. Take a look: ${url}` },
        { id: 'curiosity', label: 'Curiosity', text: `Most folks never look up what their local hospital actually charges. Here's ${hosp}'s full price list, broken down by code: ${url}` },
        { id: 'educational', label: 'Educational', text: `Federal law made hospital prices public in 2021. ${hosp}'s data is here, sortable by procedure code: ${url}` },
        { id: 'personal', label: 'Personal', text: `Going to ${hosp}? Here's what they charge — before insurance: ${url}` },
      ],
    });
  }

  // 3) Procedure code
  if (p.code && p.codeType) {
    const slug = codeUrlSlug(p.codeType);
    const url = abs(`/${slug}/${p.code}`);
    out.push({
      id: 'code',
      category: 'codes',
      label: `${typeLabel} ${p.code}`,
      icon: <Hash className="w-3.5 h-3.5" />,
      url,
      description: `National pricing for ${typeLabel} ${p.code}.`,
      templates: [
        { id: 'outrage', label: 'Outrage', text: `${typeLabel} ${p.code} costs anywhere from $50 to $1,800+ across US hospitals. Same code. Same procedure. Wild: ${url}` },
        { id: 'curiosity', label: 'Curiosity', text: `How much does ${typeLabel} ${p.code} cost? Depends — by 35× or more. The national spread: ${url}` },
        { id: 'educational', label: 'Educational', text: `${typeLabel} ${p.code} pricing across the US, broken down by setting × unit × dosage: ${url}` },
      ],
    });
  }

  // 4) State hospital rankings
  if (p.hospitalState) {
    const url = abs(`/hospital-rankings/${p.hospitalState}`);
    out.push({
      id: 'state-rankings',
      category: 'hospitals',
      label: `${p.hospitalState} rankings`,
      icon: <MapPin className="w-3.5 h-3.5" />,
      url,
      description: `Every ${p.hospitalState} hospital ranked by markup.`,
      templates: [
        { id: 'outrage', label: 'Outrage', text: `${p.hospitalState} hospitals, ranked by how much they bill above national averages. The list: ${url}` },
        { id: 'curiosity', label: 'Curiosity', text: `Wonder how your ${p.hospitalState} hospital compares to others in the state? See where it ranks: ${url}` },
        { id: 'civic', label: 'Civic', text: `${p.hospitalState}'s hospital pricing is public — and varied. The full ranking: ${url}` },
      ],
    });
  }

  // 5) Scenario detail
  if (p.scenarioId && p.scenarioSlug) {
    const url = abs(`/scenarios/${p.scenarioId}/${p.scenarioSlug}`);
    out.push({
      id: 'scenario',
      category: 'scenarios',
      label: 'This scenario',
      icon: <Stethoscope className="w-3.5 h-3.5" />,
      url,
      description: 'A specific real-life medical event with national pricing.',
      templates: [
        { id: 'outrage', label: 'Outrage', text: `Same procedure, 5-50× different prices across US hospitals. Real-life pricing for this scenario: ${url}` },
        { id: 'educational', label: 'Educational', text: `Real-life cost breakdown — what this medical event actually costs at hospitals nationwide: ${url}` },
        { id: 'personal', label: 'Personal', text: `If this is your situation — here are the actual hospital prices, plus Connect-to-Care resources: ${url}` },
      ],
    });
  }

  // ── Always-on broader scopes ─────────────────────────────────────────────

  // 6) Hospital rankings (national)
  out.push({
    id: 'all-rankings',
    category: 'hospitals',
    label: 'All US rankings',
    icon: <Building2 className="w-3.5 h-3.5" />,
    url: abs('/hospital-rankings'),
    description: 'Every US hospital, ranked nationally.',
    templates: [
      { id: 'outrage', label: 'Outrage', text: `Most-marked-up hospitals in America, ranked. Some bill 200%+ above national averages: ${abs('/hospital-rankings')}` },
      { id: 'curiosity', label: 'Curiosity', text: `4,800+ US hospitals ranked by how much they charge vs national median prices. Find yours: ${abs('/hospital-rankings')}` },
      { id: 'civic', label: 'Civic', text: `Hospital pricing is public. Here's the full national ranking — by markup, code type, or scenario: ${abs('/hospital-rankings')}` },
    ],
  });

  // 7) Worst offenders
  out.push({
    id: 'worst-offenders',
    category: 'hospitals',
    label: 'Worst offenders',
    icon: <Flame className="w-3.5 h-3.5" />,
    url: abs('/worst-hospital-offenders'),
    description: 'Top US hospitals by markup over national.',
    templates: [
      { id: 'outrage', label: 'Outrage', text: `The hospitals charging the most above national averages — the official shortlist: ${abs('/worst-hospital-offenders')}` },
      { id: 'civic', label: 'Civic', text: `Some US hospitals consistently bill far above the national average. Here's who: ${abs('/worst-hospital-offenders')}` },
    ],
  });

  // 8) Extreme price gouging
  out.push({
    id: 'extreme-gouging',
    category: 'hospitals',
    label: 'Extreme gouging',
    icon: <AlertTriangle className="w-3.5 h-3.5" />,
    url: abs('/extreme-price-gouging'),
    description: 'The wildest single-procedure markups uncovered.',
    templates: [
      { id: 'outrage', label: 'Outrage', text: `The most extreme single-procedure markups uncovered across US hospitals — many at 1000%+ over national: ${abs('/extreme-price-gouging')}` },
    ],
  });

  // 9) Scenarios overview
  out.push({
    id: 'all-scenarios',
    category: 'scenarios',
    label: 'All scenarios',
    icon: <Stethoscope className="w-3.5 h-3.5" />,
    url: abs('/scenarios'),
    description: '11,000+ medical events with national pricing.',
    templates: [
      { id: 'curiosity', label: 'Curiosity', text: `Knee replacement, MRI, ER visit — what each costs nationally. 11,000+ medical scenarios with real prices: ${abs('/scenarios')}` },
      { id: 'personal', label: 'Personal', text: `Going through a medical event? Find your situation in 11,000+ scenarios — with actual hospital prices: ${abs('/scenarios')}` },
    ],
  });

  // 10) International hub
  out.push({
    id: 'international',
    category: 'international',
    label: 'International',
    icon: <Globe className="w-3.5 h-3.5" />,
    url: abs('/international'),
    description: 'How US prices compare to the rest of the world.',
    templates: [
      { id: 'outrage', label: 'Outrage', text: `Americans pay 3–10× more for the same procedures as people in any other developed country. The data: ${abs('/international')}` },
      { id: 'curiosity', label: 'Curiosity', text: `A CT scan in Germany: $300. Same scan in the US: $3,000. Same machine. Same procedure. Why?: ${abs('/international')}` },
    ],
  });

  // 11) $100 challenge
  out.push({
    id: 'hundred-dollar',
    category: 'international',
    label: '$100 challenge',
    icon: <DollarSign className="w-3.5 h-3.5" />,
    url: abs('/international/100-dollar-challenge'),
    description: 'What can $100 buy in hospital care, country by country?',
    templates: [
      { id: 'outrage', label: 'Outrage', text: `What can $100 buy you in healthcare? In the US: not much. Elsewhere: a lot more. Country-by-country: ${abs('/international/100-dollar-challenge')}` },
      { id: 'curiosity', label: 'Curiosity', text: `The $100 healthcare challenge — what every country buys for the same dollar amount: ${abs('/international/100-dollar-challenge')}` },
    ],
  });

  // 12) Patient stories
  out.push({
    id: 'stories',
    category: 'engage',
    label: 'Patient stories',
    icon: <MessageCircle className="w-3.5 h-3.5" />,
    url: abs('/stories'),
    description: 'Real billing stories from Americans.',
    templates: [
      { id: 'personal', label: 'Personal', text: `Real billing stories from Americans navigating hospital costs — and you can share yours: ${abs('/stories')}` },
    ],
  });

  // 13) Watchdog
  out.push({
    id: 'watchdog',
    category: 'investigate',
    label: 'Price watchdog',
    icon: <Search className="w-3.5 h-3.5" />,
    url: p.hospitalState ? abs(`/watchdog/${p.hospitalState}`) : abs('/watchdog'),
    description: 'Find pricing outliers across hospitals.',
    templates: [
      { id: 'civic', label: 'Civic', text: `The pricing watchdog — finds hospitals that are extreme outliers. Helpful for journalists, researchers, advocates: ${p.hospitalState ? abs(`/watchdog/${p.hospitalState}`) : abs('/watchdog')}` },
    ],
  });

  // 14) AHP home
  out.push({
    id: 'home',
    category: 'site',
    label: 'AHP overall',
    icon: <Home className="w-3.5 h-3.5" />,
    url: abs('/'),
    description: 'The whole tool.',
    templates: [
      { id: 'civic', label: 'Civic', text: `${DOMAIN} — every US hospital's published prices, in one searchable place: ${abs('/')}` },
      { id: 'educational', label: 'Educational', text: `Federal law made hospital prices public in 2021. Most folks never look. This site makes it easy: ${abs('/')}` },
    ],
  });

  return out;
}

// Category metadata for grouping pills
const CATEGORY_LABELS: Record<ShareScope['category'], { label: string; icon: React.ReactNode }> = {
  breakdown: { label: 'Current', icon: <Layers className="w-3 h-3" /> },
  hospitals: { label: 'Hospitals', icon: <Building2 className="w-3 h-3" /> },
  codes: { label: 'Codes', icon: <Hash className="w-3 h-3" /> },
  scenarios: { label: 'Scenarios', icon: <Stethoscope className="w-3 h-3" /> },
  international: { label: 'International', icon: <Globe className="w-3 h-3" /> },
  investigate: { label: 'Investigate', icon: <Search className="w-3 h-3" /> },
  engage: { label: 'Stories', icon: <Users className="w-3 h-3" /> },
  site: { label: 'Site', icon: <Home className="w-3 h-3" /> },
};

// ────────────────────────────────────────────────────────────────────────────
export const ShareDignityModal = (props: ShareDignityModalProps) => {
  const { t } = useTranslation(['homepage']);
  const { isOpen, onClose, defaultScopeId } = props;
  const [modelViewerReady, setModelViewerReady] = useState(false);
  const [useGlb, setUseGlb] = useState(false);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;
    const big = window.matchMedia('(min-width: 768px)').matches;
    setUseGlb(big);
    if (!big) return;
    if ((customElements as any).get('model-viewer')) { setModelViewerReady(true); return; }
    ensureModelViewer()
      .then(() => customElements.whenDefined('model-viewer').then(() => setModelViewerReady(true)))
      .catch(() => setModelViewerReady(false));
  }, [isOpen]);

  // Build all available scopes from the props
  const scopes = useMemo(() => buildScopes(props), [
    props.shareUrl, props.hospitalId, props.hospitalName, props.hospitalState,
    props.codeType, props.code, props.codeTypeLabel, props.markupPercentage,
    props.scenarioId, props.scenarioSlug,
  ]);

  const [selectedScopeId, setSelectedScopeId] = useState(defaultScopeId || scopes[0]?.id);
  const selectedScope = useMemo(() => scopes.find(s => s.id === selectedScopeId) || scopes[0], [scopes, selectedScopeId]);

  // Reset selected scope to default when modal re-opens or scopes change
  useEffect(() => {
    if (isOpen) setSelectedScopeId(defaultScopeId || scopes[0]?.id);
  }, [isOpen, defaultScopeId, scopes]);

  // Templates from current scope
  const templates = selectedScope?.templates || [];
  const [selectedTemplateId, setSelectedTemplateId] = useState(templates[0]?.id || 'outrage');
  const [draft, setDraft] = useState(templates[0]?.text || '');
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

  // When scope changes, reset draft to first template of new scope
  useEffect(() => {
    if (!selectedScope) return;
    const t = selectedScope.templates.find(t => t.id === selectedTemplateId) || selectedScope.templates[0];
    if (t) setDraft(t.text);
  }, [selectedScope, selectedTemplateId]);

  const pickScope = (id: string) => {
    setSelectedScopeId(id);
    const newScope = scopes.find(s => s.id === id);
    if (newScope) {
      // Try to keep the same template id; fall back to first
      const sameKind = newScope.templates.find(t => t.id === selectedTemplateId);
      const first = sameKind || newScope.templates[0];
      setSelectedTemplateId(first.id);
      setDraft(first.text);
    }
  };

  const pickTemplate = (id: string) => {
    setSelectedTemplateId(id as any);
    const t = templates.find(t => t.id === id);
    if (t) setDraft(t.text);
  };

  const url = selectedScope?.url || '';

  // Group scopes by category for the pill row
  const scopesByCategory = useMemo(() => {
    const out: Record<string, ShareScope[]> = {};
    for (const s of scopes) {
      if (!out[s.category]) out[s.category] = [];
      out[s.category].push(s);
    }
    return out;
  }, [scopes]);

  // Platforms
  const platforms: Array<{ id: string; label: string; color: string; icon?: React.ReactNode; href: () => string; note?: string }> = [
    { id: 'x', label: 'X (Twitter)', color: '#000', href: () => `https://twitter.com/intent/tweet?text=${enc(draft)}` },
    { id: 'bluesky', label: 'Bluesky', color: '#0085FF', href: () => `https://bsky.app/intent/compose?text=${enc(draft)}` },
    { id: 'threads', label: 'Threads', color: '#101010', href: () => `https://www.threads.net/intent/post?text=${enc(draft)}` },
    { id: 'reddit', label: 'Reddit', color: '#FF4500', href: () => `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(draft.slice(0, 300))}` },
    { id: 'facebook', label: 'Facebook', color: '#1877F2', href: () => `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`, note: 'Pre-fill text not supported — copy text first' },
    { id: 'linkedin', label: 'LinkedIn', color: '#0A66C2', href: () => `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`, note: 'Pre-fill text not supported — copy text first' },
    { id: 'email', label: 'Email', color: '#6B7280', icon: <Mail className="w-4 h-4" />, href: () => `mailto:?subject=${enc('A peek at hospital chargemaster prices')}&body=${enc(draft)}` },
    { id: 'sms', label: 'Text', color: '#10B981', icon: <MessageSquare className="w-4 h-4" />, href: () => `sms:?&body=${enc(draft)}` },
  ];

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      window.prompt('Copy this text:', draft);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 sm:rounded-2xl">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#FFF8E7] to-[#FAF9F6] dark:from-[#1a2332] dark:to-[#0a1628] border-b border-border p-6">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              {useGlb && modelViewerReady ? (
                // @ts-ignore - model-viewer web component
                <model-viewer
                  src="/consumer/dignity_bill.glb"
                  alt={t('homepage:homeBeta.shareDignity.dignityAlt')}
                  shadow-intensity="0.6"
                  exposure="1.1"
                  interaction-prompt="none"
                  camera-orbit="0deg 75deg 105%"
                  style={{ width: 110, height: 130, background: 'transparent', display: 'block' } as React.CSSProperties}
                />
              ) : (
                <div className="w-[100px] h-[100px] rounded-full overflow-hidden border-[3px] border-[#C5A55A] bg-[#FFF8E7] shadow-lg">
                  <img src="/consumer/dignity-transparent.webp" alt="Dignity" className="w-full h-full object-cover object-top" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <DialogHeader>
                <DialogTitle className="text-2xl font-serif">{t('homepage:homeBeta.shareDignity.title')}</DialogTitle>
                <DialogDescription className="mt-1 text-sm">
                  <Trans
                    i18nKey="homepage:homeBeta.shareDignity.description"
                    components={{ em: <em /> }}
                  />
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Scope picker — what to share */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">
              {t('homepage:homeBeta.shareDignity.whatToShare')}
            </label>
            <div className="space-y-2">
              {(['breakdown','hospitals','codes','scenarios','international','investigate','engage','site'] as const).map(cat => {
                const items = scopesByCategory[cat];
                if (!items?.length) return null;
                const meta = CATEGORY_LABELS[cat];
                return (
                  <div key={cat} className="flex items-start gap-2">
                    <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider w-20 pt-1.5 flex items-center gap-1 flex-shrink-0">
                      {meta.icon} {meta.label}
                    </span>
                    <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                      {items.map(s => {
                        const active = s.id === selectedScopeId;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => pickScope(s.id)}
                            title={s.description}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                              active
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background text-foreground/70 border-border hover:border-primary/60 hover:text-foreground'
                            }`}
                          >
                            {s.icon}
                            <span>{s.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedScope?.description && (
              <p className="text-[11px] text-muted-foreground italic mt-2 ml-[88px]">
                {selectedScope.description}
              </p>
            )}
          </div>

          {/* Tone picker */}
          {templates.length > 1 && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">{t('homepage:homeBeta.shareDignity.tone')}</label>
              <div className="flex flex-wrap gap-2">
                {templates.map(t => {
                  const active = t.id === selectedTemplateId;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => pickTemplate(t.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground border-border hover:border-primary/60 hover:text-foreground'
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Editable draft */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">{t('homepage:homeBeta.shareDignity.editMessage')}</label>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              className="w-full p-3 text-sm border border-border rounded-md bg-background resize-y focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-muted-foreground">
                {draft.length > 280
                  ? t('homepage:homeBeta.shareDignity.charsOver', { n: draft.length, over: draft.length - 280 })
                  : t('homepage:homeBeta.shareDignity.charsUnder', { n: draft.length, remaining: 280 - draft.length })}
              </span>
              <button
                type="button"
                onClick={copyDraft}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
                  copyState === 'copied'
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-400'
                    : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {copyState === 'copied' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copyState === 'copied' ? t('homepage:homeBeta.shareDignity.copied') : t('homepage:homeBeta.shareDignity.copyText')}
              </button>
            </div>
          </div>

          {/* Platforms */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">{t('homepage:homeBeta.shareDignity.shareTo')}</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {platforms.map(p => (
                <a
                  key={p.id}
                  href={p.href()}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={p.note}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-border hover:border-primary hover:bg-primary/5 transition-colors text-foreground"
                  style={{ borderLeftColor: p.color, borderLeftWidth: 3 }}
                >
                  {p.icon}
                  {p.label}
                </a>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 italic">
              {t('homepage:homeBeta.shareDignity.fbLiNote')}
            </p>
          </div>

          {/* Direct URL copy */}
          <div className="pt-3 border-t border-border">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">{t('homepage:homeBeta.shareDignity.orSendLink')}</label>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={url}
                onClick={(e) => (e.currentTarget as HTMLInputElement).select()}
                className="flex-1 p-2 text-xs font-mono border border-border rounded-md bg-muted/30 text-muted-foreground"
              />
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(url);
                    setCopyState('copied');
                    setTimeout(() => setCopyState('idle'), 2000);
                  } catch {
                    window.prompt('Copy this link:', url);
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border border-border hover:bg-muted transition-colors flex-shrink-0"
              >
                <Share2 className="w-3 h-3" /> Copy link
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
